import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { InlineSelect, STATUS_COLORS, URGENCY_COLORS, ACTION_COLORS } from './StatusBadge';
import { getHotFiles, createHotFile, updateHotFile, deleteHotFile, completeHotFile, assignHotFile, getTeamMembers, suggestActions, summarizeHotFileNotes } from '../lib/api';
import { Plus, PencilSimple, Trash, Sparkle, CaretDown, CaretUp, MagnifyingGlass, CheckCircle, UserSwitch, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUSES = ['Urgent', 'Needs Completed Today', 'Pending', 'Done', 'On Hold/No Action'];
const URGENCIES = ['Critical', 'High', 'Normal', 'Low'];
const ACTIONS = ['Completed', 'Needs Follow Up', 'Waiting on Borrower'];
const TASK_TYPES = ['Submission', 'Escalation'];
const LOAN_OFFICERS = ['Eric Hamilton', 'Tom Eboli', 'Fabian Herrera', 'Mike Pelagio'];
const GROUP_ORDER = ['SA', 'MC', 'AVP', 'LC', 'PM'];

const TASK_TYPE_COLORS = {
  'Submission': 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30',
  'Escalation': 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30',
};

const FILTERS = [{ id: 'all', label: 'All' }, { id: 'urgent', label: 'Urgent/High' }, { id: 'pending', label: 'Pending' }, { id: 'done', label: 'Done' }, { id: 'follow_up', label: 'Follow Up' }];

const emptyForm = { sa_group: '', borrower: '', status: 'Pending', urgency: 'Normal', action_item: 'Needs Follow Up', task_type: 'Submission', notes: '', closing_date: '', assigned_to: '', loan_officer: '' };

// ─── Sub-components ───

function FilterControls({ searchQuery, setSearchQuery, filterMode, setFilterMode, onAdd }) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-8 w-[200px] h-9 text-sm border-border bg-card" data-testid="search-files-input" />
        </div>
        <div className="flex gap-0.5 bg-muted rounded-md p-0.5">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilterMode(f.id)} data-testid={`filter-${f.id}`}
              className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${filterMode === f.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <Button onClick={onAdd} className="bg-primary text-primary-foreground hover:opacity-90 h-9" data-testid="add-file-btn">
        <Plus size={16} className="mr-1.5" /> Add File
      </Button>
    </div>
  );
}

function FileRow({ file, showLO, loOptions, assignOptions, editingNotes, setEditingNotes, onInlineUpdate, onNotesBlur, onAssign, onComplete, onAiSuggest, onEdit, onDelete, aiLoading, aiResults, onSummarize, summarizing }) {
  const [notesExpanded, setNotesExpanded] = useState(false);
  const notesText = editingNotes[file.id] !== undefined ? editingNotes[file.id] : file.notes;
  const isLong = (notesText || '').length > 30;
  const isVeryLong = (notesText || '').length > 200;

  return (
    <TableRow key={file.id} className="border-border hover:bg-muted/30 transition-colors" data-testid={`file-row-${file.id}`}>
      <TableCell className="font-medium text-foreground pl-4 text-sm">{file.borrower}</TableCell>
      {showLO && (
        <TableCell>
          <InlineSelect value={file.loan_officer || 'None'} options={loOptions}
            onChange={(v) => onInlineUpdate(file.id, 'loan_officer', v === 'None' ? '' : v)}
            testId={`inline-lo-${file.id}`} />
        </TableCell>
      )}
      {showLO && (
        <TableCell>
          <InlineSelect value={file.task_type || 'Submission'} options={TASK_TYPES}
            onChange={(v) => onInlineUpdate(file.id, 'task_type', v)}
            testId={`inline-type-${file.id}`} colorMap={TASK_TYPE_COLORS} />
        </TableCell>
      )}
      <TableCell><InlineSelect value={file.status} options={STATUSES} onChange={(v) => onInlineUpdate(file.id, 'status', v)} testId={`inline-status-${file.id}`} colorMap={STATUS_COLORS} /></TableCell>
      <TableCell><InlineSelect value={file.urgency} options={URGENCIES} onChange={(v) => onInlineUpdate(file.id, 'urgency', v)} testId={`inline-urgency-${file.id}`} colorMap={URGENCY_COLORS} /></TableCell>
      <TableCell><InlineSelect value={file.action_item} options={ACTIONS} onChange={(v) => onInlineUpdate(file.id, 'action_item', v)} testId={`inline-action-${file.id}`} colorMap={ACTION_COLORS} /></TableCell>
      <TableCell><InlineSelect value={file.assigned_to || 'Unassigned'} options={assignOptions} onChange={(v) => onInlineUpdate(file.id, 'assigned_to', v === 'Unassigned' ? '' : v)} testId={`inline-assigned-${file.id}`} /></TableCell>
      <TableCell className="max-w-[220px]">
        <div>
          {isVeryLong && (
            <div className="mb-1 rounded-md border border-violet-500/20 bg-violet-500/5 px-2 py-1" data-testid={`ai-summary-${file.id}`}>
              <div className="flex items-start gap-1.5">
                <Sparkle size={10} weight="fill" className="text-violet-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  {file.ai_note_summary ? (
                    <p className="text-[10px] text-violet-200 leading-snug"><span className="font-semibold text-violet-300">Clara: </span>{file.ai_note_summary}</p>
                  ) : (
                    <button onClick={() => onSummarize(file.id)} disabled={summarizing[file.id]}
                      className="text-[10px] text-violet-400 hover:text-violet-300 font-semibold" data-testid={`summarize-btn-${file.id}`}>
                      {summarizing[file.id] ? 'Clara reading notes...' : 'Summarize with Clara'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {notesExpanded ? (
            <textarea
              value={notesText}
              onChange={(e) => setEditingNotes(p => ({ ...p, [file.id]: e.target.value }))}
              onBlur={(e) => onNotesBlur(file.id, e.target.value)}
              className="w-full text-xs bg-muted/50 border border-border rounded-md p-2 text-foreground focus:border-primary focus:outline-none resize-none min-h-[80px]"
              data-testid={`inline-notes-expanded-${file.id}`}
            />
          ) : (
            <input type="text" value={notesText}
              onChange={(e) => setEditingNotes(p => ({ ...p, [file.id]: e.target.value }))}
              onBlur={(e) => onNotesBlur(file.id, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              className="w-full text-xs bg-transparent border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-0.5 text-foreground truncate"
              data-testid={`inline-notes-${file.id}`} />
          )}
          {isLong && (
            <button onClick={() => setNotesExpanded(p => !p)}
              className="flex items-center gap-0.5 text-[10px] text-primary hover:text-primary/80 font-medium mt-0.5 transition-colors"
              data-testid={`expand-notes-${file.id}`}>
              {notesExpanded ? <><CaretUp size={10} /> Less</> : <><CaretDown size={10} /> More</>}
            </button>
          )}
        </div>
        {(aiResults[file.id] || file.ai_suggestion) && (
          <div className="mt-1 p-1.5 bg-amber-500/10 border border-amber-500/20 rounded text-[10px] text-amber-800 dark:text-amber-400 leading-snug">
            <Sparkle size={10} weight="fill" className="inline mr-1" />
            {aiResults[file.id] ? aiResults[file.id].map(s => s.action).join('; ') : file.ai_suggestion?.substring(0, 80)}
          </div>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{file.closing_date || '-'}</TableCell>
      <TableCell className="pr-4">
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="icon" onClick={() => onAssign(file)} className="h-7 w-7 text-blue-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20" title="Assign Task" data-testid={`assign-btn-${file.id}`}>
            <UserSwitch size={14} />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onComplete(file.id, file.borrower)} className="h-7 w-7 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20" title="Complete" data-testid={`complete-btn-${file.id}`}>
            <CheckCircle size={14} weight="fill" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onAiSuggest(file.id)} disabled={aiLoading[file.id]} className="h-7 w-7 text-amber-500 hover:text-amber-600" data-testid={`ai-suggest-btn-${file.id}`}>
            {aiLoading[file.id] ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-amber-500 border-t-transparent" /> : <Sparkle size={14} weight="fill" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onEdit(file)} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid={`edit-file-btn-${file.id}`}>
            <PencilSimple size={14} />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(file.id)} className="h-7 w-7 text-red-400 hover:text-red-600" data-testid={`delete-file-btn-${file.id}`}>
            <Trash size={14} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function LoanOfficerSection({ sa, saFiles, members, collapsedSAs, toggleSA, showLO, loOptions, assignOptions, editingNotes, setEditingNotes, onInlineUpdate, onNotesBlur, onAssign, onComplete, onAiSuggest, onEdit, onDelete, onAddToGroup, aiLoading, aiResults, onSummarize, summarizing }) {
  const groupMembers = members.filter(m => m.group === sa);
  const groupLabel = groupMembers.length > 0 ? groupMembers.map(m => m.name).join(', ') : sa;
  const collapsed = collapsedSAs[sa];

  return (
    <Card key={sa} className="border border-border bg-card animate-fade-up" data-testid={`sa-group-${sa.toLowerCase()}`}>
      <CardHeader className="py-3 px-4">
        <button onClick={() => toggleSA(sa)} className="flex items-center justify-between w-full" data-testid={`toggle-sa-${sa.toLowerCase()}`}>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary text-primary-foreground text-sm font-bold">{sa}</span>
            <div>
              <span className="text-lg font-semibold text-foreground">{groupLabel}</span>
              <span className="text-sm text-muted-foreground ml-2">{saFiles.length} file{saFiles.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          {collapsed ? <CaretDown size={18} className="text-muted-foreground" /> : <CaretUp size={18} className="text-muted-foreground" />}
        </button>
      </CardHeader>
      {!collapsed && (
        <CardContent className="pt-0 px-0">
          {saFiles.length === 0 ? (
            <div className="py-6 px-4 text-center">
              <p className="text-base text-muted-foreground mb-3">No files assigned to this group</p>
              <Button variant="outline" size="sm" onClick={() => onAddToGroup(sa)}
                className="border-border text-xs" data-testid={`add-file-to-${sa.toLowerCase()}`}>
                <Plus size={14} className="mr-1" /> Add File to {sa}
              </Button>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pl-4">Borrower</TableHead>
                  {showLO && <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Loan Officer</TableHead>}
                  {showLO && <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Type</TableHead>}
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Status</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Urgency</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Action</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Assigned</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Notes</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Closing</TableHead>
                  <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pr-4 w-[130px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {saFiles.map(file => (
                  <FileRow key={file.id} file={file} showLO={showLO} loOptions={loOptions} assignOptions={assignOptions}
                    editingNotes={editingNotes} setEditingNotes={setEditingNotes}
                    onInlineUpdate={onInlineUpdate} onNotesBlur={onNotesBlur}
                    onAssign={onAssign} onComplete={onComplete} onAiSuggest={onAiSuggest}
                    onEdit={onEdit} onDelete={onDelete} aiLoading={aiLoading} aiResults={aiResults}
                    onSummarize={onSummarize} summarizing={summarizing} />
                ))}
              </TableBody>
            </Table>
          </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ─── Main Component ───

export default function HotFilesBoard({ initialFilter }) {
  const [files, setFiles] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [assignFrom, setAssignFrom] = useState('');
  const [editingFile, setEditingFile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSAs, setCollapsedSAs] = useState({});
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});
  const [summarizing, setSummarizing] = useState({});
  const [filterMode, setFilterMode] = useState(initialFilter || 'all');
  const [editingNotes, setEditingNotes] = useState({});

  const loOptions = useMemo(() => ['None', ...LOAN_OFFICERS], []);
  const assignOptions = useMemo(() => ['Unassigned', ...members.map(m => m.initials)], [members]);

  const loadData = useCallback(async () => {
    try {
      const [f, m] = await Promise.all([getHotFiles(), getTeamMembers()]);
      setFiles(f); setMembers(m);
    } catch (e) { logError('Hot files load error:', e); toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); const iv = setInterval(loadData, 5000); return () => clearInterval(iv); }, [loadData]);
  useEffect(() => { if (initialFilter) setFilterMode(initialFilter); }, [initialFilter]);

  const handleInlineUpdate = async (fileId, field, value) => {
    try {
      await updateHotFile(fileId, { [field]: value });
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, [field]: value } : f));
    } catch (e) { toast.error('Update failed'); }
  };

  const getFilteredFiles = useCallback(() => {
    let result = files;
    if (filterMode === 'urgent') result = result.filter(f => f.urgency === 'Critical' || f.urgency === 'High');
    else if (filterMode === 'pending') result = result.filter(f => f.status === 'Pending');
    else if (filterMode === 'done') result = result.filter(f => f.status === 'Done');
    else if (filterMode === 'follow_up') result = result.filter(f => f.action_item === 'Needs Follow Up');
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(f => f.borrower.toLowerCase().includes(q) || f.notes.toLowerCase().includes(q) || f.sa_group.toLowerCase().includes(q) || (f.loan_officer || '').toLowerCase().includes(q));
    }
    return result;
  }, [files, filterMode, searchQuery]);

  const groups = useMemo(() => {
    const filtered = getFilteredFiles();
    const g = {};
    if (filterMode === 'all' && !searchQuery) {
      const uniqueGroups = [...new Set(members.map(m => m.group).filter(Boolean))];
      uniqueGroups.forEach(grp => { g[grp] = []; });
    }
    filtered.forEach(f => { if (!g[f.sa_group]) g[f.sa_group] = []; g[f.sa_group].push(f); });
    Object.keys(g).forEach(k => g[k].sort((a, b) => ({ Critical: 0, High: 1, Normal: 2, Low: 3 }[a.urgency] || 2) - ({ Critical: 0, High: 1, Normal: 2, Low: 3 }[b.urgency] || 2)));
    const ordered = {};
    GROUP_ORDER.forEach(k => { if (g[k] !== undefined) ordered[k] = g[k]; });
    Object.keys(g).forEach(k => { if (!ordered[k]) ordered[k] = g[k]; });
    return ordered;
  }, [getFilteredFiles, filterMode, searchQuery, members]);

  const handleSave = async () => {
    if (!form.borrower.trim() || !form.sa_group.trim()) { toast.error('Required fields missing'); return; }
    try {
      if (editingFile) { await updateHotFile(editingFile.id, form); toast.success('Updated'); }
      else { await createHotFile(form); toast.success('Created'); }
      setDialogOpen(false); setEditingFile(null); setForm(emptyForm); loadData();
    } catch (e) { toast.error('Save failed'); }
  };

  const handleComplete = async (id, borrower) => {
    try { await completeHotFile(id); toast.success(`${borrower} moved to history`); loadData(); } catch (e) { toast.error('Failed'); }
  };

  const openAssignDialog = (file) => {
    setAssignTarget(file); setAssignTo(file.assigned_to || ''); setAssignFrom(''); setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (!assignTo) { toast.error('Select team member'); return; }
    try {
      await assignHotFile(assignTarget.id, { to_member: assignTo, from_member: assignFrom });
      toast.success(`Assigned to ${assignTo} - notification sent`);
      setAssignDialogOpen(false); loadData();
    } catch (e) { toast.error('Assign failed'); }
  };

  const handleDelete = async (id) => { try { await deleteHotFile(id); toast.success('Deleted'); loadData(); } catch (e) { toast.error('Failed'); } };
  const handleEdit = (file) => {
    setEditingFile(file);
    setForm({ sa_group: file.sa_group, borrower: file.borrower, status: file.status, urgency: file.urgency, action_item: file.action_item, task_type: file.task_type || 'Submission', notes: file.notes, closing_date: file.closing_date || '', assigned_to: file.assigned_to || '', loan_officer: file.loan_officer || '' });
    setDialogOpen(true);
  };
  const handleAiSuggest = async (fileId) => {
    setAiLoading(p => ({ ...p, [fileId]: true }));
    try { const r = await suggestActions(fileId); setAiResults(p => ({ ...p, [fileId]: r.suggestions })); toast.success('Suggestions ready'); loadData(); }
    catch (e) { toast.error('Failed'); }
    finally { setAiLoading(p => ({ ...p, [fileId]: false })); }
  };
  const handleSummarize = async (fileId) => {
    setSummarizing(p => ({ ...p, [fileId]: true }));
    try {
      const r = await summarizeHotFileNotes(fileId);
      if (r.summary) {
        setFiles(prev => prev.map(f => f.id === fileId ? { ...f, ai_note_summary: r.summary } : f));
        toast.success('Clara summarized the note');
      } else {
        toast.info('Note not long enough to summarize');
      }
    } catch (e) { toast.error('Clara could not summarize'); }
    finally { setSummarizing(p => ({ ...p, [fileId]: false })); }
  };
  const toggleSA = (sa) => setCollapsedSAs(p => ({ ...p, [sa]: !p[sa] }));
  const handleNotesBlur = async (fileId, val) => { if (editingNotes[fileId] !== undefined) { await handleInlineUpdate(fileId, 'notes', val); setEditingNotes(p => { const n = {...p}; delete n[fileId]; return n; }); } };

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="files-loading"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="hot-files-board">
      <FilterControls searchQuery={searchQuery} setSearchQuery={setSearchQuery} filterMode={filterMode} setFilterMode={setFilterMode}
        onAdd={() => { setEditingFile(null); setForm(emptyForm); setDialogOpen(true); }} />

      {Object.entries(groups).map(([sa, saFiles]) => (
        <LoanOfficerSection key={sa} sa={sa} saFiles={saFiles} members={members}
          collapsedSAs={collapsedSAs} toggleSA={toggleSA} showLO={sa === 'SA'}
          loOptions={loOptions} assignOptions={assignOptions}
          editingNotes={editingNotes} setEditingNotes={setEditingNotes}
          onInlineUpdate={handleInlineUpdate} onNotesBlur={handleNotesBlur}
          onAssign={openAssignDialog} onComplete={handleComplete} onAiSuggest={handleAiSuggest}
          onEdit={handleEdit} onDelete={handleDelete}
          onAddToGroup={(g) => { setEditingFile(null); setForm({ ...emptyForm, sa_group: g }); setDialogOpen(true); }}
          aiLoading={aiLoading} aiResults={aiResults}
          onSummarize={handleSummarize} summarizing={summarizing} />
      ))}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="file-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editingFile ? 'Edit Hot File' : 'Add New Hot File'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Group</label>
                <Select value={form.sa_group} onValueChange={(v) => setForm(p => ({ ...p, sa_group: v }))}>
                  <SelectTrigger className="border-border h-9" data-testid="input-sa-initials"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{GROUP_ORDER.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Borrower</label>
                <Input value={form.borrower} onChange={(e) => setForm(p => ({ ...p, borrower: e.target.value }))} className="border-border h-9" data-testid="input-borrower" /></div>
            </div>
            {form.sa_group === 'SA' && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Loan Officer</label>
                  <Select value={form.loan_officer || 'none'} onValueChange={(v) => setForm(p => ({ ...p, loan_officer: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="border-border h-9" data-testid="select-loan-officer"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{LOAN_OFFICERS.map(lo => <SelectItem key={lo} value={lo}>{lo}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Task Type</label>
                  <Select value={form.task_type} onValueChange={(v) => setForm(p => ({ ...p, task_type: v }))}>
                    <SelectTrigger className="border-border h-9" data-testid="select-task-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}><SelectTrigger className="border-border h-9" data-testid="select-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Urgency</label>
                <Select value={form.urgency} onValueChange={(v) => setForm(p => ({ ...p, urgency: v }))}><SelectTrigger className="border-border h-9" data-testid="select-urgency"><SelectValue /></SelectTrigger><SelectContent>{URGENCIES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Action</label>
                <Select value={form.action_item} onValueChange={(v) => setForm(p => ({ ...p, action_item: v }))}><SelectTrigger className="border-border h-9" data-testid="select-action-item"><SelectValue /></SelectTrigger><SelectContent>{ACTIONS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Closing Date</label>
                <Input type="date" value={form.closing_date} onChange={(e) => setForm(p => ({ ...p, closing_date: e.target.value }))} className="border-border h-9" data-testid="input-closing-date" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Assigned To</label>
                <Select value={form.assigned_to || 'none'} onValueChange={(v) => setForm(p => ({ ...p, assigned_to: v === 'none' ? '' : v }))}><SelectTrigger className="border-border h-9" data-testid="select-assigned-to"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Unassigned</SelectItem>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="border-border min-h-[60px]" data-testid="input-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border" data-testid="cancel-file-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-primary text-primary-foreground" data-testid="save-file-btn">{editingFile ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><UserSwitch size={20} className="text-blue-500" /> Assign Task</DialogTitle></DialogHeader>
          {assignTarget && <p className="text-sm text-muted-foreground">Assign <strong className="text-foreground">{assignTarget.borrower}</strong> to a team member. They will be notified.</p>}
          <div className="space-y-3">
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">From</label>
              <Select value={assignFrom} onValueChange={setAssignFrom}><SelectTrigger className="border-border h-9" data-testid="assign-from-select"><SelectValue placeholder="Who is assigning?" /></SelectTrigger><SelectContent>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Assign To</label>
              <Select value={assignTo} onValueChange={setAssignTo}><SelectTrigger className="border-border h-9" data-testid="assign-to-select"><SelectValue placeholder="Select member" /></SelectTrigger><SelectContent>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleAssign} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-assign-btn">Assign & Notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
